import React, { useState, useRef, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { sendAgentQuery } from '../services/agentService';
import { Box, TextField, Button, Paper, Typography, CircularProgress } from '@mui/material';
import SendIcon from '@mui/icons-material/Send';
import ReactMarkdown from 'react-markdown';
import remarkGfm from 'remark-gfm';

const Chat = () => {
    const [messages, setMessages] = useState([]);
    const [input, setInput] = useState('');
    const [loading, setLoading] = useState(false);
    const messagesEndRef = useRef(null);
    const navigate = useNavigate();

    useEffect(() => {
        // Check if user is logged in
        const userData = localStorage.getItem('userData');
        if (!userData) {
            navigate('/login');
            return;
        }
    }, [navigate]);

    const scrollToBottom = () => {
        messagesEndRef.current?.scrollIntoView({ behavior: "smooth" });
    };

    useEffect(() => {
        scrollToBottom();
    }, [messages]);

    const handleSubmit = async (e) => {
        e.preventDefault();
        if (!input.trim()) return;

        const userMessage = input.trim();
        setInput('');
        setLoading(true);

        // Add user message to chat
        setMessages(prev => [...prev, { type: 'user', content: userMessage }]);

        try {
            const response = await sendAgentQuery(userMessage);
            
            if (response.status === 'error') {
                throw new Error(response.message);
            }

            // Handle different response types
            if (response.response_type === 'image') {
                // For image responses
                setMessages(prev => [...prev, { 
                    type: 'agent',
                    content: response.response.message,
                    images: response.response.images
                }]);
            } else {
                // For text responses
                setMessages(prev => [...prev, { 
                    type: 'agent',
                    content: response.response
                }]);
            }
        } catch (error) {
            if (error.message === 'User not logged in') {
                navigate('/login');
            } else {
                setMessages(prev => [...prev, { 
                    type: 'error', 
                    content: error.message || 'Failed to get response from agent' 
                }]);
            }
        } finally {
            setLoading(false);
        }
    };

    const renderMessage = (message) => {
        return (
            <Box
                sx={{
                    display: 'flex',
                    justifyContent: message.type === 'user' ? 'flex-end' : 'flex-start',
                    mb: 2,
                    width: '100%'
                }}
            >
                <Paper
                    sx={{
                        p: 2,
                        maxWidth: message.images ? '90%' : '70%',
                        width: message.images ? 'auto' : 'auto',
                        bgcolor: message.type === 'user' ? 'primary.light' : 'background.paper',
                        color: message.type === 'user' ? 'white' : 'text.primary',
                        borderRadius: 2
                    }}
                >
                    {message.type === 'user' ? (
                        <Typography>{message.content}</Typography>
                    ) : (
                        <Box 
                            className="markdown-content"
                            sx={{ 
                                '& h1, & h2, & h3, & h4, & h5, & h6': {
                                    mt: 2,
                                    mb: 1,
                                    fontWeight: 'bold'
                                },
                                '& p': {
                                    mb: 1
                                },
                                '& ul, & ol': {
                                    pl: 2,
                                    mb: 1
                                },
                                '& li': {
                                    mb: 0.5
                                },
                                '& code': {
                                    bgcolor: 'grey.100',
                                    p: 0.5,
                                    borderRadius: 1,
                                    fontFamily: 'monospace'
                                },
                                '& pre': {
                                    bgcolor: 'grey.100',
                                    p: 1,
                                    borderRadius: 1,
                                    overflow: 'auto'
                                },
                                '& blockquote': {
                                    borderLeft: 4,
                                    borderColor: 'grey.300',
                                    pl: 2,
                                    ml: 0,
                                    my: 1
                                },
                                '& table': {
                                    borderCollapse: 'collapse',
                                    width: '100%',
                                    mb: 2
                                },
                                '& th, & td': {
                                    border: 1,
                                    borderColor: 'grey.300',
                                    p: 1
                                },
                                '& a': {
                                    color: 'primary.main',
                                    textDecoration: 'none',
                                    '&:hover': {
                                        textDecoration: 'underline'
                                    }
                                }
                            }}
                        >
                            <ReactMarkdown 
                                remarkPlugins={[remarkGfm]}
                                components={{
                                    // Override default components for better styling
                                    p: ({node, ...props}) => <Typography {...props} />,
                                    h1: ({node, ...props}) => <Typography variant="h4" {...props} />,
                                    h2: ({node, ...props}) => <Typography variant="h5" {...props} />,
                                    h3: ({node, ...props}) => <Typography variant="h6" {...props} />,
                                    h4: ({node, ...props}) => <Typography variant="subtitle1" {...props} />,
                                    h5: ({node, ...props}) => <Typography variant="subtitle2" {...props} />,
                                    h6: ({node, ...props}) => <Typography variant="subtitle2" {...props} />
                                }}
                            >
                                {message.content}
                            </ReactMarkdown>
                        </Box>
                    )}
                    
                    {message.images && (
                        <Box 
                            sx={{ 
                                mt: 2,
                                display: 'flex',
                                flexDirection: 'column',
                                gap: 2,
                                alignItems: 'center'
                            }}
                        >
                            {message.images.map((img, index) => (
                                <Box 
                                    key={index} 
                                    sx={{ 
                                        width: '100%',
                                        maxWidth: '512px',
                                        display: 'flex',
                                        justifyContent: 'center'
                                    }}
                                >
                                    <img 
                                        src={`data:image/png;base64,${img.data}`}
                                        alt=""
                                        style={{ 
                                            width: '100%',
                                            height: 'auto',
                                            borderRadius: '8px',
                                            objectFit: 'contain'
                                        }}
                                    />
                                </Box>
                            ))}
                        </Box>
                    )}
                </Paper>
            </Box>
        );
    };

    return (
        <Box 
            sx={{ 
                height: '100%',
                display: 'flex', 
                flexDirection: 'column',
                bgcolor: '#f5f5f5'
            }}
        >
            {/* Messages container */}
            <Box 
                sx={{ 
                    flexGrow: 1, 
                    overflow: 'auto', 
                    p: 2,
                    display: 'flex',
                    flexDirection: 'column'
                }}
            >
                {messages.map((message, index) => (
                    <div key={index}>
                        {renderMessage(message)}
                    </div>
                ))}
                <div ref={messagesEndRef} />
            </Box>
            
            {/* Input area */}
            <Box 
                component="form" 
                onSubmit={handleSubmit} 
                sx={{ 
                    p: 2, 
                    borderTop: 1, 
                    borderColor: 'divider',
                    bgcolor: 'white'
                }}
            >
                <Box sx={{ display: 'flex', gap: 1 }}>
                    <TextField
                        fullWidth
                        value={input}
                        onChange={(e) => setInput(e.target.value)}
                        placeholder="Type your message..."
                        disabled={loading}
                        variant="outlined"
                        size="medium"
                        sx={{
                            bgcolor: 'white',
                            '& .MuiOutlinedInput-root': {
                                borderRadius: 2
                            }
                        }}
                    />
                    <Button
                        type="submit"
                        variant="contained"
                        disabled={loading}
                        endIcon={loading ? <CircularProgress size={20} /> : <SendIcon />}
                        sx={{
                            borderRadius: 2,
                            px: 3,
                            minWidth: '100px'
                        }}
                    >
                        Send
                    </Button>
                </Box>
            </Box>
        </Box>
    );
};

export default Chat; 